import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Timeline.module.scss";
import { ShiftRow } from "../ShiftsRow";
import { Text } from "@medoma/components/Text/Text";
import clsx from "clsx";
import type { ShiftsWithContents } from "@/api/Shifts";
import { usePlanningViewToolbar } from "../usePlanningViewToolbar";
import {
  useFilteredShiftsWithContents,
  type ShiftsBySection,
} from "../useFilteredShiftsWithContents";
import { usePatientOrActivityTitleFilter } from "../PlanningContext";
import { useRevealNonMatchingShifts } from "../useRevealNonMatchingShifts";
import { type ShiftWithFilteredContents } from "../shiftWithFilteredContents";
import { ShowNonMatchingShiftsButton } from "../ShowNonMatchingShiftsButton";
import NoResults from "@/components/NoResults/NoResults";

const TimelineShiftList = ({
  shiftsWithFilteredContents,
  className,
  isExpanded,
}: {
  shiftsWithFilteredContents: ShiftWithFilteredContents[];
  className: string | undefined;
  isExpanded: boolean;
}) => {
  if (shiftsWithFilteredContents.length === 0) {
    return null;
  }

  return (
    <ul className={className}>
      {shiftsWithFilteredContents.map((shiftWithFilteredContents) => (
        <ShiftRow
          key={shiftWithFilteredContents.shiftWithContents.shift.id}
          shiftWithContents={shiftWithFilteredContents.shiftWithContents}
          matchedItemIds={shiftWithFilteredContents.matchedItemIds}
          isExpanded={isExpanded}
        />
      ))}
    </ul>
  );
};

const CategorizedShiftRows = ({
  shiftsBySection,
  isExpanded,
}: {
  shiftsBySection: ShiftsBySection;
  isExpanded: boolean;
}) => (
  <>
    <TimelineShiftList
      shiftsWithFilteredContents={shiftsBySection.currentUsersShifts}
      className={clsx(styles.shiftsList, styles.currentUserShifts)}
      isExpanded={isExpanded}
    />
    <TimelineShiftList
      shiftsWithFilteredContents={shiftsBySection.staffedShifts}
      className={styles.shiftsList}
      isExpanded={isExpanded}
    />
    <TimelineShiftList
      shiftsWithFilteredContents={shiftsBySection.notStaffedShifts}
      className={clsx(styles.shiftsList, styles.notStaffedShifts)}
      isExpanded={isExpanded}
    />
  </>
);

export const Timeline = ({
  shiftsWithContents,
}: {
  shiftsWithContents: ShiftsWithContents;
}) => {
  const { t } = useLingui();
  const { isExpanded } = usePlanningViewToolbar();
  const { value: filterValue } = usePatientOrActivityTitleFilter();
  const { areNonMatchingShiftsRevealed, revealNonMatchingShifts } =
    useRevealNonMatchingShifts(filterValue);
  const {
    matching,
    nonMatching,
    isFilterActive,
    hasNonMatchingShifts,
    numberOfNonMatchingShifts,
    noShiftsMatchSearch: noRelevantShiftsMatchSearch,
  } = useFilteredShiftsWithContents(shiftsWithContents.relevant);
  const numberOfShiftsWithContentsBeforeSelectedTimeFilter =
    shiftsWithContents.before.length;
  const numberOfShiftsWithContentsAfterSelectedTimeFilter =
    shiftsWithContents.after.length;

  return (
    <section className={styles.shiftsSection}>
      {numberOfShiftsWithContentsBeforeSelectedTimeFilter >= 1 ? (
        <div className={clsx(styles.hiddenShiftsMessage, styles.before)}>
          <Text element="span" size="small">
            <Trans>
              {numberOfShiftsWithContentsBeforeSelectedTimeFilter} tidigare
              arbetspass har dolts av tidsinställningarna
            </Trans>
          </Text>
        </div>
      ) : null}

      {/* Shifts withing selected timespan */}
      {noRelevantShiftsMatchSearch ? (
        <NoResults
          message={t`Inga arbetspass matchar sökningen`}
          padding={24}
        />
      ) : (
        <CategorizedShiftRows
          shiftsBySection={matching}
          isExpanded={isExpanded}
        />
      )}

      {/* Shifts not matching current search filter - hidden by default, visible on action */}
      {isFilterActive && hasNonMatchingShifts ? (
        areNonMatchingShiftsRevealed ? (
          <CategorizedShiftRows
            shiftsBySection={nonMatching}
            isExpanded={isExpanded}
          />
        ) : (
          <div className={styles.revealNonMatchingShifts}>
            <ShowNonMatchingShiftsButton
              numberOfNonMatchingShifts={numberOfNonMatchingShifts}
              onClick={revealNonMatchingShifts}
            />
          </div>
        )
      ) : null}
      {numberOfShiftsWithContentsAfterSelectedTimeFilter >= 1 ? (
        <div className={clsx(styles.hiddenShiftsMessage, styles.after)}>
          <Text element="span" size="small">
            <Trans>
              {numberOfShiftsWithContentsAfterSelectedTimeFilter} senare
              arbetspass har dolts av tidsinställningarna
            </Trans>
          </Text>
        </div>
      ) : null}
    </section>
  );
};
